// src/services/WordThumbnailService.ts
import mammoth from 'mammoth';

/**
 * Generates a thumbnail for Word documents using Mammoth.js to extract real content
 * @param file Word document file (.doc or .docx)
 * @returns Data URL containing the thumbnail image
 */
export async function generateWordThumbnail(file: File): Promise<string> {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 124;
    canvas.height = 140;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    
    // Create thumbnail with a white background
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, 124, 140);
    
    // Get filename for the header
    const fileName = file.name;
    let displayName = fileName;
    const nameParts = fileName.split('.');
    const ext = nameParts.pop() || '';
    const baseName = nameParts.join('.');
    const maxBaseLength = 8; // Allow fewer base characters to fit extension
    displayName = baseName.length > maxBaseLength
      ? baseName.substring(0, maxBaseLength) + '...' + (ext || '')
      : baseName + (ext ? '.' + ext : '');
    
    // Add Word blue header
    ctx.fillStyle = '#2b579a'; // Word blue
    ctx.fillRect(0, 0, 124, 25); // Make header slightly taller for larger text
    
    // Display filename in the header
    ctx.fillStyle = 'white';
    ctx.font = 'bold 14px Lucida Grande';
    ctx.textAlign = 'center';
    ctx.fillText(displayName, 62, 17); // Display filename
    
    // Use Mammoth.js to extract text content from the Word file
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = result.value;
    
    // Draw document content from the actual Word file
    ctx.fillStyle = '#444';
    ctx.font = '16px Lucida Grande';
    ctx.textAlign = 'left';
    
    // Process the extracted text
    const processedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = processedText.split('\n').filter(line => line.trim().length > 0).slice(0, 3);
    
    lines.forEach((line, i) => {
      const words = line.split(' ').filter(word => word.trim().length > 0);
      const displayWords = words.slice(0, 4);
      let displayText = displayWords.join(' ');
      
      if (displayWords.length < words.length) {
        displayText += '...';
      }
      
      // Limit characters per line for display
      if (displayText.length > 12) {
        displayText = displayText.substring(0, 10) + '...';
      }
      
      const yPosition = 45 + (i * 22);
      ctx.fillText(displayText, 5, yPosition);
    });
    
    ctx.strokeStyle = '#e0e0e0';
    ctx.strokeRect(0, 0, 124, 140);
    
    // Display file size
    if (file.size) {
      const formatSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
      };
      
      ctx.fillStyle = '#555';
      ctx.font = '16px Lucida Grande';
      ctx.textAlign = 'center';
      ctx.fillText(formatSize(file.size), 62, 130);
    }
    
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch (error) {
    console.error('Error creating Word thumbnail:', error);
    
    // If we fail to extract text with Mammoth.js, fall back to a generic Word thumbnail
    const canvas = document.createElement('canvas');
    canvas.width = 124;
    canvas.height = 140;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, 124, 140);
    
    // Get filename for the header in fallback case
    const fileName = file.name;
    let displayName = fileName;
    const nameParts = fileName.split('.');
    const ext = nameParts.pop() || '';
    const baseName = nameParts.join('.');
    const maxBaseLength = 8; // Allow fewer base characters to fit extension
    displayName = baseName.length > maxBaseLength
      ? baseName.substring(0, maxBaseLength) + '...' + (ext || '')
      : baseName + (ext ? '.' + ext : '');
    
    // Show the filename in the header
    ctx.fillStyle = '#2b579a'; // Word blue
    ctx.fillRect(0, 0, 124, 25);
    
    ctx.fillStyle = 'white';
    ctx.font = 'bold 14px Lucida Grande';
    ctx.textAlign = 'center';
    ctx.fillText(displayName, 62, 17);
    
    ctx.strokeStyle = '#e0e0e0';
    ctx.strokeRect(0, 0, 124, 140);
    
    ctx.fillStyle = '#444';
    ctx.font = '16px Lucida Grande';
    ctx.textAlign = 'center';
    ctx.fillText('WORD', 62, 60);
    
    // Display file size with larger font
    if (file.size) {
      const formatSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
      };
      
      ctx.fillStyle = '#555';
      ctx.font = '16px Lucida Grande';
      ctx.textAlign = 'center';
      ctx.fillText(formatSize(file.size), 62, 130);
    }
    
    return canvas.toDataURL('image/jpeg', 0.9);
  }
}